import { useState } from 'react'
import { createPortal } from 'react-dom'
import {
  X,
  LayoutDashboard,
  FolderKanban,
  ClipboardCheck,
  FileWarning,
  FileText,
  Send,
  Files,
  Keyboard,
  Wrench,
  ChevronRight,
  ChevronDown,
  Workflow,
  BookOpen,
} from 'lucide-react'

type SectionKey = 'workflow' | 'dashboard' | 'projects' | 'checklists' | 'issues' | 'submittals' | 'cxreport' | 'documents' | 'shortcuts' | null

const sections: { key: SectionKey; icon: typeof LayoutDashboard; label: string; content: string[] }[] = [
  {
    key: 'workflow',
    icon: Workflow,
    label: 'Commissioning Workflow',
    content: [
      'CxRunner follows the standard commissioning (Cx) workflow:',
      '1. Design Review & Cx Plan — CxA reviews designs, develops Cx plan, creates testing checklists.',
      '2. Pre-Functional Assurance Testing (PFAT) — Equipment start-up, TAB work, controls checkout, inspect installed work.',
      '3. Functional Performance Testing (FPT) — Test all equipment, simulate operating conditions, validate sequences of operations.',
      '4. Training — Training documentation review, contractor training, O&M manual review.',
      '5. Cx Report & Systems Manual — Final deliverables summarizing process and findings.',
      '6. 10-Month Follow-Up — Warranty commissioning to verify continued proper operation.',
    ],
  },
  {
    key: 'dashboard',
    icon: LayoutDashboard,
    label: 'Dashboard',
    content: [
      'The Dashboard gives you an at-a-glance view of all commissioning activity.',
      'Active Projects — Count of projects currently in progress.',
      'Checklist Completion — Overall percentage across all active checklists.',
      'Open Issues — Counts with status breakdowns.',
      'Overdue Alerts — Items past their due date that need attention (shown in red).',
      'Recent Activity — Timeline of the latest actions across all projects.',
    ],
  },
  {
    key: 'projects',
    icon: FolderKanban,
    label: 'Projects',
    content: [
      'Projects are the top-level container for all commissioning work.',
      'Each project has Systems (HVAC, Electrical, Plumbing, etc.) organized in a hierarchy.',
      'Systems contain Equipment — individual pieces of gear being commissioned.',
      'The Project Detail page has tabs for Overview, Systems, Equipment, Checklists, Issues, Submittals, and Activity.',
      'Click any equipment tag to see its detail page with linked checklists and issues.',
    ],
  },
  {
    key: 'checklists',
    icon: ClipboardCheck,
    label: 'Checklists',
    content: [
      'Checklists are created from templates and assigned to specific equipment.',
      'Templates define the inspection items — questions with response types (Pass/Fail, Yes/No, Text, Number).',
      'When executing a checklist, fill in responses for each item. Items can be marked Pass or Fail.',
      'Failing an item reveals a quick-action button to create an Issue directly from the failed item, pre-filled with context.',
      'Completion percentage updates automatically as items are answered.',
    ],
  },
  {
    key: 'issues',
    icon: FileWarning,
    label: 'Issues Log',
    content: [
      'The Issues Log tracks broader project issues: RFIs, deficiencies, observations, non-conformances, and change orders.',
      'Issues have categories (Design, Installation, Controls, TAB, etc.) and phases (Design, PFAT, FPT, Training, Closeout, Warranty).',
      'Assign a responsible party and due date. Track resolution notes when closing.',
      'Overdue issues show a red clock icon. Export to CSV for meeting distribution.',
      'The sidebar badge shows how many issues are currently overdue.',
    ],
  },
  {
    key: 'submittals',
    icon: Send,
    label: 'Submittals',
    content: [
      'Submittals track documents sent for review — TAB reports, controls checkouts, shop drawings, O&M manuals, etc.',
      'Each submittal goes through review cycles with statuses: Approved, Approved As Noted, Rejected, or Resubmit.',
      'The current review number increments with each cycle.',
      'Link submittals to specific systems or equipment for traceability.',
      'Export to CSV for submittal logs required by contract.',
    ],
  },
  {
    key: 'cxreport',
    icon: FileText,
    label: 'Cx Report',
    content: [
      'The Cx Report page generates a comprehensive commissioning report using AI.',
      'Select a project to pull in all systems, equipment, checklists, issues, and submittals.',
      'The report includes executive summary, system narratives, test results, outstanding items, and recommendations.',
      'Use Quick Download on the Project Detail page for a fast export.',
    ],
  },
  {
    key: 'documents',
    icon: Files,
    label: 'Documents',
    content: [
      'The Documents page stores project files — drawings, specs, reports, photos, and more.',
      'Upload files with a category and description for easy retrieval.',
      'Files are stored in cloud storage (R2) and served on demand.',
      'Filter by project or category to find what you need.',
    ],
  },
  {
    key: 'shortcuts',
    icon: Keyboard,
    label: 'Keyboard Shortcuts',
    content: [
      'Ctrl+K / Cmd+K — Open global search. Search across projects, equipment, issues, and submittals.',
      'Arrow Keys — Navigate search results up/down.',
      'Enter — Open selected search result.',
      'Escape — Close search, modals, or the help menu.',
    ],
  },
]

export function HelpMenu({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [expanded, setExpanded] = useState<SectionKey>('workflow')

  if (!isOpen) return null

  const toggleSection = (key: SectionKey) => {
    setExpanded(prev => prev === key ? null : key)
  }

  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal modal-panel modal-lg modal-scrollable"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 520 }}
      >
        <div className="modal-header">
          <div className="d-flex align-center gap-3">
            <div style={{
              width: 36, height: 36, borderRadius: 'var(--radius-md)',
              background: 'var(--accent-blue-soft)', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
            }}>
              <BookOpen size={20} color="var(--accent-blue)" />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>Help & Guide</h2>
              <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)', marginTop: 2 }}>
                CxRunner Commissioning Management
              </div>
            </div>
          </div>
          <button className="modal-close" onClick={onClose}><X size={20} /></button>
        </div>

        <div className="modal-body" style={{ padding: 0 }}>
          <div style={{
            background: 'var(--accent-blue-soft)', borderRadius: 'var(--radius-md)',
            padding: 'var(--spacing-4)', marginBottom: 'var(--spacing-4)',
            border: '1px solid rgba(59, 130, 246, 0.2)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-2)' }}>
              <Wrench size={16} color="var(--accent-blue)" />
              <span style={{ fontWeight: 'var(--font-weight-semibold)' as unknown as number, color: 'var(--accent-blue)' }}>
                Quick Start
              </span>
            </div>
            <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
              Create a <strong>Project</strong>, add <strong>Systems</strong> and <strong>Equipment</strong>,
              then run <strong>Checklists</strong> against each piece of equipment.
              Failed items become <strong>Issues</strong>.
              Track <strong>Submittals</strong> for document reviews.
              Generate the final <strong>Cx Report</strong> when ready.
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {sections.map((section) => {
              const isExpanded = expanded === section.key
              return (
                <div key={section.key}>
                  <button
                    onClick={() => toggleSection(section.key)}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)',
                      padding: 'var(--spacing-3) var(--spacing-3)',
                      background: isExpanded ? 'var(--bg-hover)' : 'transparent',
                      border: 'none', borderRadius: 'var(--radius-md)',
                      cursor: 'pointer', color: 'var(--text-primary)',
                      transition: 'background var(--transition-fast)',
                      fontSize: 'var(--font-size-base)',
                    }}
                    onMouseEnter={(e) => { if (!isExpanded) e.currentTarget.style.background = 'var(--bg-hover)' }}
                    onMouseLeave={(e) => { if (!isExpanded) e.currentTarget.style.background = 'transparent' }}
                  >
                    <section.icon size={18} color={isExpanded ? 'var(--accent-blue)' : 'var(--text-muted)'} />
                    <span style={{ flex: 1, textAlign: 'left', fontWeight: isExpanded ? 600 : 400 }}>
                      {section.label}
                    </span>
                    {isExpanded
                      ? <ChevronDown size={16} color="var(--text-muted)" />
                      : <ChevronRight size={16} color="var(--text-muted)" />
                    }
                  </button>
                  {isExpanded && (
                    <div style={{
                      padding: 'var(--spacing-2) var(--spacing-3) var(--spacing-4) calc(var(--spacing-3) + 18px + var(--spacing-3))',
                    }}>
                      {section.content.map((line, i) => (
                        <p key={i} style={{
                          margin: 0, marginBottom: i < section.content.length - 1 ? 'var(--spacing-2)' : 0,
                          fontSize: 'var(--font-size-sm)', color: 'var(--text-secondary)',
                          lineHeight: 1.6,
                        }}>
                          {line.match(/^(\d+)\.\s/) ? (
                            <>
                              <span style={{ color: 'var(--accent-blue)', fontWeight: 600 }}>
                                {line.match(/^(\d+)\./)?.[0]}
                              </span>
                              {line.replace(/^\d+\./, '')}
                            </>
                          ) : line.includes(' — ') && !line.startsWith('CxRunner') ? (
                            <>
                              <strong style={{ color: 'var(--text-primary)' }}>
                                {line.split(' — ')[0]}
                              </strong>
                              {' — '}{line.split(' — ').slice(1).join(' — ')}
                            </>
                          ) : (
                            line
                          )}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>

        <div style={{
          padding: 'var(--spacing-4)', borderTop: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)',
          flexShrink: 0,
        }}>
          <span>Press <kbd style={{
            background: 'var(--bg-tertiary)', padding: '2px 6px',
            borderRadius: 'var(--radius-sm)', fontSize: '11px',
            border: '1px solid var(--border)',
          }}>?</kbd> to toggle help</span>
          <span>CxRunner v1.0</span>
        </div>
      </div>
    </div>, document.body
  )
}
